/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type AbstractControl, type ValidationErrors } from '@angular/forms';
import { type BaseRequestOptionsArgs, type CommonCountResponse, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { FileSystemModeE } from 'app/common/constants';
import { type TaskOptionsArgs, TaskService } from 'app/common/services';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { type DataTurboClient, type DataTurboShare, type Dtree, type FileSystem } from '../../../../../common/types/mo';
import { type CreateDataTurboParams } from '../share';

@Injectable()
export class DataTurboShareService {
    constructor(
        private http: HttpService,
        private taskService: TaskService,
    ) { }

    /**
     * 批量查询dataturbo共享rest
     */
    getBatchRest(vstoreId?: string, fsId?: string, dtreeId?: string): Rest {
        const rest = this.http.makeUrl('DataTurboShare.getBatch', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        if (fsId) {
            rest.setFilters([
                { field: 'fsId', matchMode: '::', value: fsId },
            ]);
        }
        if (dtreeId) {
            rest.setFilters([
                { field: 'dtreeId', matchMode: '::', value: dtreeId },
            ]);
        }
        return rest;
    }

    /**
     * 批量查询dataturbo共享count rest
     */
    countRest(vstoreId?: string, fsId?: string, dtreeId?: string): Rest {
        const rest = this.http.makeUrl('DataTurboShare.count', undefined, true);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        if (fsId) {
            rest.setFilters([
                { field: 'fsId', matchMode: '::', value: fsId },
            ]);
        }
        if (dtreeId) {
            rest.setFilters([
                { field: 'dtreeId', matchMode: '::', value: dtreeId },
            ]);
        }
        return rest;
    }

    /**
     * 批量查询dataturbo共享
     */
    getBatch(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<DataTurboClient[]> {
        const rest = this.getBatchRest(vstoreId);
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: DataTurboClient[] }) => data),
            );
    }

    /**
     * 批量查询dataturbo共享数量(用于卡片创建查询权限数量)
     */
    getCount(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<string> {
        const rest = this.countRest(vstoreId);
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: string }) => data),
            );
    }

    /**
     * 查询dataturbo用户Batch
     */
    getUserBatch(vstoreId: string, shareId?: string): Observable<DataTurboClient[]> {
        const rest = this.http.makeUrl('DataTurboUser.getBatch');
        rest.setParams({ vstoreId });
        shareId && rest.setFilters([{ field: 'shareId', matchMode: '::', value: shareId }]);
        return this.http.get(rest)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询dataturbo用户Batch rest
     */
    getUserBatchRest(vstoreId: string, shareId?: string): Rest {
        const rest = this.http.makeUrl('DataTurboUser.getBatch');
        rest.setParams({ vstoreId });
        shareId && rest.setFilters([{ field: 'shareId', matchMode: '::', value: shareId }]);
        return rest;
    }

    /**
     * 查询单个dataturbo共享
     */
    get(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<DataTurboShare> {
        const rest = this.http.makeUrl('DataTurboShare.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }

    /**
     * 查询dataturbo用户Batch
     */
    getSharePermissionBatch(id: number | string, vstoreId: string): Observable<DataTurboClient[]> {
        const rest = this.http.makeUrl('DataTurboUser.getBatch');
        rest.setParams({ vstoreId });
        rest.setFilters([{ field: 'shareId', matchMode: '::', value: id }]);
        return this.http.get(rest)
            .pipe(
                map(({ data }: { data: DataTurboClient[] }) => data),
            );
    }

    /**
     * 批量查询dataturbo用户count rest
     */
    usercountRest(vstoreId: string): Rest {
        return this.http.makeUrl('DataTurboUser.count')
            .setParams({ vstoreId });
    }

    /**
     * 查询dataturbo用户count
     */
    getUserCount(userName: number | string, vstoreId: string): Observable<CommonCountResponse> {
        const rest = this.http.makeUrl('DataTurboUser.count')
            .setParams({ userName, vstoreId });
        return this.http.get(rest).pipe(
            map(({ data }: { data: { COUNT: string } }) => data),
        );
    }

    /**
     * 查询可添加权限的用户rest
     */
    getAvaliableUserBatchRest(vstoreUserId?: string | number, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboUser.getAvailbleUserBatch');
        // 指定返回UTC时间，timeConversion取值：0：utc，1：带时区偏移时间，默认1
        rest.setParams({ timeConversion: 0, vstoreId: vstoreUserId });
        return this.http.get(rest, options);
    }

    /**
     * 不加权限创建dataturbo
     */
    create(params: CreateDataTurboParams, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboShare.post');
        return this.http.post(rest, params, options);
    }

    /**
     * 加权限创建dataturbo，走omtask
     */
    createByOmtask(objName: string, params: CreateDataTurboParams, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboShare.post', 'V2');
        return this.taskService.post('createDataTurboShare', objName, rest, params, options);
    }

    /**
     * 为data turbo共享增加权限
     */
    addClients(objName: string, params: any, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboClient.add', 'V2');
        return this.taskService.post('addDataTurboClientShare', objName, rest, params, options);
    }

    /**
     * 修改data turbo共享
     */
    modify(id: number | string, params: DataTurboShare, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboShare.put').format(id);
        return this.http.put(rest, params, options);
    }

    /**
     * 删除data turbo共享
     */
    delete(id: number | string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboShare.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    /**
     * 删除data turbo共享权限
     */
    deletePermission(id: number | string, userName: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('DataTurboUser.delete').format(id);
        rest.setParams({ userName });
        return this.http.delete(rest, options);
    }

    /**
     * dataTurbo共享名校验
     */
    dataTurboShareNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }
        const reg = /["\/\\\[\]:|<>\+;,\?\*=]+/;
        const reservedNameArr = ['ipc$', 'autohome', '~', 'print$'];
        const valid = !(reg.test(control.value) || reservedNameArr.includes(control.value.toLocaleLowerCase()));
        return valid ? null : { dataTurboShareName: true };
    }

    /**
     * 查询对应名称的文件系统
     */
    getFSOnName(name: string, options?: BaseRequestOptionsArgs): Observable<FileSystem[]> {
        const rest = this.http.makeUrl('fileSystem.getBatch')
            .setFilters([{ field: 'NAME', matchMode: '::', value: name }]);
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: FileSystem[] }) => data),
            );
    }

    /**
     * 获取对应租户DataTurbo智能推荐数据
     */
    getSmartguiDataTurbo(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('smartgui.getDataTurboShare', 'V2').format(vstoreId);
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: FileSystem }) => data),
            );
    }

    /**
     * 批量查询文件系统rest
     */
    getBatchFSRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.getBatch', undefined, true);
        rest.setFilters([{
            field: 'SUBTYPE', matchMode: '!!', value: FileSystemModeE.SMART_MOBILITY_INNER_FILE_SYSTEM,
        }]);

        if (vstoreId) {
            rest.setParams({ vstoreId });
        }

        return rest;
    }

    /**
     * 批量查询文件系统count rest
     */
    getBatchFSCountRest(vstoreId?: string): Rest {
        const rest = this.http.makeUrl('fileSystem.count');
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return rest;
    }

    /**
     * 查询单个文件系统
     */
    getFileSystem(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: FileSystem }) => data),
            );
    }

    /**
     * 查询单个dtree
     */
    getDtree(dtreeId: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<Dtree> {
        const rest = this.http.makeUrl('dtree.get').format(dtreeId);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }: { data: Dtree }) => data),
            );
    }

    getBatchDtreeRest(fileSystem?: FileSystem): Rest {
        const rest = this.http.makeUrl('dtree.getBatch');
        if (fileSystem) {
            rest.setParams({
                PARENTID: fileSystem.ID,
                vstoreId: fileSystem.vstoreId,
            });
        }
        return rest;
    }
}
